import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Notify } from '../../Utility/Notify';
import { UpdateOneCommunicationData } from '../../Redux/Actions/CommunicationWay_Action';
import { useTheme } from '@mui/material/styles';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const IconWarning = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconComm = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

/* ─── CSS (injected once) ────────────────────────────────────────────────── */
const CSS = `
  .cw-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid transparent; cursor: pointer;
    background: transparent;
    transition: background 0.18s, border-color 0.18s, color 0.18s, transform 0.15s;
    color: #7B829A; text-decoration: none;
  }
  .cw-btn:hover { transform: translateY(-1px); }
  .cw-btn.edit  { border-color: #E8EAF0; background: #F7F8FC; color: #1A3ECF; }
  .cw-btn.edit:hover { background: #EEF1FF; border-color: #1A3ECF; }
  .cw-btn.del   { border-color: #FFE4E4; background: #FFF5F5; color: #E53935; }
  .cw-btn.del:hover { background: #FFE4E4; border-color: #E53935; }

  .cw-overlay {
    position: fixed; inset: 0; z-index: 999999;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0,0,0,0.75);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    animation: cw-fadein 0.22s ease-out both;
  }
  @keyframes cw-fadein { from { opacity: 0; } to { opacity: 1; } }

  .cw-card {
    width: 100%; max-width: 500px; margin: 0 20px;
    border-radius: 28px; padding: 40px 32px 36px;
    background: #ffffff;
    box-shadow: 0 40px 100px rgba(0,0,0,0.35);
    position: relative;
    animation: cw-slidein 0.28s cubic-bezier(0.34,1.2,0.64,1) both;
  }
  .cw-card.dark-m { background: linear-gradient(135deg,#1a1f2e 0%,#141829 100%); color: #E4E8F5; }
  @keyframes cw-slidein {
    from { opacity: 0; transform: translateY(28px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .cw-close {
    position: absolute; top: 18px; right: 18px;
    width: 36px; height: 36px; border-radius: 12px;
    border: 1px solid #E8EAF0; background: #F7F8FC;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    color: #7B829A; transition: all 0.2s;
  }
  .cw-close:hover { background: #FFE4E4; border-color: #E53935; color: #E53935; transform: scale(1.05); }
  .dark-m .cw-close { background: #252D4A; border-color: #3A4570; color: #9099c0; }
  .dark-m .cw-close:hover { background: #4a2525; border-color: #E53935; color: #E53935; }

  .cw-icon {
    width: 84px; height: 84px; border-radius: 50%;
    background: linear-gradient(135deg,#FFF3F3,#FFE4E4);
    border: 2px solid #FFBDBD;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 22px; color: #E53935;
    box-shadow: 0 8px 24px rgba(229,57,53,0.18);
  }
  .dark-m .cw-icon { background: linear-gradient(135deg,#2a1a1a,#3a2525); border-color: #5a3535; }

  .cw-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 22px; font-weight: 700;
    color: #1C2340; text-align: center;
    margin: 0 0 10px; letter-spacing: -0.3px;
  }
  .dark-m .cw-title { color: #E4E8F5; }

  .cw-sub {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; color: #7B829A;
    text-align: center; margin: 0 0 28px; line-height: 1.65;
  }

  .cw-badge {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 7px 16px; border-radius: 40px;
    background: #F0F4FF; border: 1px solid #D6E0FF;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; color: #1A3ECF;
    margin-bottom: 28px;
  }
  .dark-m .cw-badge { background: #252D4A; border-color: #3A4570; color: #7DA4FF; }

  .cw-actions { display: flex; gap: 12px; }

  .cw-cancel {
    flex: 1; padding: 14px 0; border-radius: 14px;
    border: 1.5px solid #E8EAF0; background: #F7F8FC;
    font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600;
    color: #1C2340; cursor: pointer; transition: all 0.2s;
  }
  .cw-cancel:hover { background: #EBEDF5; border-color: #C8CADC; transform: translateY(-1px); }
  .dark-m .cw-cancel { background: #252D4A; border-color: #3A4570; color: #E4E8F5; }
  .dark-m .cw-cancel:hover { background: #2E3860; }

  .cw-confirm {
    flex: 1; padding: 14px 0; border-radius: 14px; border: none;
    background: linear-gradient(135deg,#E53935,#C62828);
    font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 700;
    color: #fff; cursor: pointer; transition: all 0.2s;
    box-shadow: 0 6px 20px rgba(229,57,53,0.3);
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .cw-confirm:hover { background: linear-gradient(135deg,#EF5350,#D32F2F); box-shadow: 0 8px 28px rgba(229,57,53,0.4); transform: translateY(-2px); }
  .cw-confirm:disabled { background: #E8EAF0; color: #B0B5C9; box-shadow: none; transform: none; cursor: not-allowed; }

  .cw-spin {
    width: 17px; height: 17px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff; border-radius: 50%;
    animation: cw-spin 0.7s linear infinite;
  }
  @keyframes cw-spin { to { transform: rotate(360deg); } }

  @media (max-width: 560px) {
    .cw-card { padding: 30px 20px 26px; border-radius: 20px; }
    .cw-title { font-size: 19px; }
    .cw-icon  { width: 68px; height: 68px; }
  }
`;

let injected = false;
const injectCSS = () => {
  if (injected) return;
  injected = true;
  const el = document.createElement('style');
  el.innerHTML = CSS;
  document.head.appendChild(el);
};

/* ─── Delete Modal via Portal ────────────────────────────────────────────── */
const DeleteModal = ({ isDark, communicationName, onClose, onConfirm, loading }) =>
  createPortal(
    <div
      className="cw-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`cw-card ${isDark ? 'dark-m' : ''}`}>

        <button className="cw-close" onClick={onClose}><IconClose /></button>

        <div className="cw-icon"><IconWarning /></div>

        <p className="cw-title">Delete Communication Way?</p>
        <p className="cw-sub">
          This action will deactivate the communication way.<br />
          You can restore it later from the admin panel.
        </p>

        {communicationName && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className="cw-badge">
              <IconComm />
              {communicationName}
            </span>
          </div>
        )}

        <div className="cw-actions">
          <button className="cw-cancel" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="cw-confirm" onClick={onConfirm} disabled={loading}>
            {loading
              ? <><div className="cw-spin" />Deleting…</>
              : <><IconTrash />Delete</>}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const CommunicationwayActions = ({ communication }) => {
  injectCSS();

  const theme   = useTheme();
  const isDark  = theme.palette.mode === 'dark';
  const dispatch = useDispatch();

  const [show,    setShow]    = useState(false);
  const [loading, setLoading] = useState(false);

  const deleteCommunicationway = async () => {
    setLoading(true);
    await dispatch(
      UpdateOneCommunicationData(communication._id, {
        iscommunicationwayactivate: 'false',
      })
    );
    setLoading(false);
    setShow(false);
    Notify('success', 'Deleted Successfully', 'success');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Link
          to={`/UpdateCommunicationway/${communication?._id}`}
          className="cw-btn edit"
          title="Edit communication way"
        >
          <IconEdit />
        </Link>
        <button
          className="cw-btn del"
          title="Delete communication way"
          onClick={() => setShow(true)}
        >
          <IconTrash />
        </button>
      </div>

      {show && (
        <DeleteModal
          isDark={isDark}
          communicationName={communication?.name}
          onClose={() => setShow(false)}
          onConfirm={deleteCommunicationway}
          loading={loading}
        />
      )}
    </>
  );
};
